import React from 'react';
import { EssayList, BookShelf, QuoteCard } from '../components/intellectual';
import '../themes/intellectual.css';

// ============================================
// Type Definitions
// ============================================
interface Essay {
    id: string;
    title: string;
    excerpt: string;
    category: string;
    date: Date;
    readTime: string;
    link: string;
}

interface Book {
    title: string;
    author: string;
    cover: string;
    note?: string;
    link?: string;
}

export interface IntellectualTemplateData {
    identity: {
        name: string;
        avatar: string;
        tagline: string;
        bio: string;
    };
    pinnedQuote?: {
        text: string;
        attribution?: string;
    };
    essays: Essay[];
    books: Book[];
    newsletter?: {
        title: string;
        description: string;
        buttonText?: string;
    };
    footerQuote?: string;
    social: {
        twitter?: string;
        substack?: string;
        linkedin?: string;
    };
}

// ============================================
// Social Icons Component
// ============================================
const SocialIcon: React.FC<{ platform: string; href: string }> = ({ platform, href }) => {
    const icons: Record<string, string> = {
        twitter: '𝕏',
        substack: '📰',
        linkedin: '💼',
    };

    return (
        <a href={href} target="_blank" rel="noopener noreferrer" title={platform}>
            {icons[platform] || '🔗'}
        </a>
    );
};

// ============================================
// Main Template Component
// ============================================
export const IntellectualTemplate: React.FC<{ data: IntellectualTemplateData }> = ({ data }) => {
    const { identity, pinnedQuote, essays, books, newsletter, footerQuote, social } = data;

    return (
        <div className="intellectual-layout" data-theme="intellectual">
            {/* Header */}
            <header className="intellectual-header">
                <div className="intellectual-header-logo">
                    {identity.name}
                </div>

                <nav className="intellectual-header-nav">
                    <a href="#essays">Essays</a>
                    <a href="#library">Library</a>
                </nav>

                <a
                    href={social.substack || social.twitter || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="intellectual-header-cta"
                >
                    Subscribe
                </a>
            </header>

            {/* Hero Section */}
            <section className="intellectual-hero">
                <img
                    src={identity.avatar}
                    alt={identity.name}
                    className="intellectual-hero-avatar"
                />
                <h1 className="intellectual-hero-name">{identity.name}</h1>
                <p className="intellectual-hero-tagline">{identity.tagline}</p>
                <p className="intellectual-hero-bio">{identity.bio}</p>
            </section>

            {/* Pinned Quote */}
            {pinnedQuote && (
                <section className="intellectual-section">
                    <QuoteCard
                        text={pinnedQuote.text}
                        attribution={pinnedQuote.attribution}
                    />
                </section>
            )}

            {/* Essays */}
            {essays.length > 0 && (
                <section id="essays" className="intellectual-section">
                    <h2 className="intellectual-section-title">Selected Essays</h2>
                    <div className="intellectual-section-divider" />
                    <EssayList essays={essays} />
                </section>
            )}

            {/* Book Shelf */}
            {books.length > 0 && (
                <section id="library" className="intellectual-section">
                    <h2 className="intellectual-section-title">From My Library</h2>
                    <div className="intellectual-section-divider" />
                    <BookShelf books={books} />
                </section>
            )}

            {/* Newsletter */}
            {newsletter && (
                <section className="intellectual-section">
                    <div className="intellectual-newsletter">
                        <h3 className="intellectual-newsletter-title">{newsletter.title}</h3>
                        <p className="intellectual-newsletter-desc">{newsletter.description}</p>
                        <form className="intellectual-newsletter-form" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="email"
                                className="intellectual-newsletter-input"
                                placeholder="your@email.com"
                            />
                            <button type="submit" className="intellectual-newsletter-btn">
                                {newsletter.buttonText || 'Subscribe'}
                            </button>
                        </form>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="intellectual-footer">
                {footerQuote && (
                    <p className="intellectual-footer-quote">"{footerQuote}"</p>
                )}
                <div className="intellectual-footer-social">
                    {social.twitter && <SocialIcon platform="twitter" href={social.twitter} />}
                    {social.substack && <SocialIcon platform="substack" href={social.substack} />}
                    {social.linkedin && <SocialIcon platform="linkedin" href={social.linkedin} />}
                </div>
                <p className="intellectual-footer-text">
                    © {new Date().getFullYear()} {identity.name} • Built with Crypto Factory
                </p>
            </footer>
        </div>
    );
};

export default IntellectualTemplate;
